import { behavior } from "@testing-library/user-event/event/behavior/registry";
import { FieldErrors, useFormContext } from "react-hook-form";
import { Button, Font } from "@moneyforward/mfx_biz_react_components";
import { CompanyNameFormField } from "@/components/molecules";
import React, { useState } from "react";
import { useIsSpMode } from "@/hooks";
import { css } from "@emotion/react";
import { color } from "@/styles/theme";
import { KanaCompanyNameFormField } from "@/components/molecules/KanaCompanyNameFormField";
import { BusinessContentFormField } from "@/components/molecules/BusinessContentFormField";
import { RepresentativeNameFormField } from "@/components/molecules/RepresentativeNameFormField";
import { BlockBuildingNameFormField } from "@/components/molecules/BlockBuildingNameFormField";
import { MunicipalityFormField } from "@/components/molecules/MunicipalityFormField";
import { CorporateNumberFormField } from "@/components/molecules/CorporateNumberFormField";
import { FoundationDateFormField } from "@/components/molecules/FoundationDateFormField";
import { CapitalFormField } from "@/components/molecules/CapitalFormField";
import { PhoneNumberFormField } from "@/components/molecules/PhoneNumberFormField";
import { PostalCodeFormField } from "@/components/molecules/PostalCodeFormField";
import { PrefecturesFormField } from "@/components/molecules/PrefecturesFormField";
import { SettlementMonthFormField } from "@/components/molecules/SettlementMonthFormField";
import { IndustriesFormField } from "@/components/molecules/IndustriesFormField";
import { StaffCountFormField } from "@/components/molecules/StaffCountFormField";
import { BranchOfficeFormField } from "@/components/molecules/BranchOfficeFormField";
import { InsuranceAgencyCodeFormField } from "@/components/molecules/InsuranceAgencyCodeFormField";
import { BankBranchOffice, BusinessType } from "@/generated/graphql";
import { CancelButton } from "@/components/atoms";
import { RepresentativeEmailAddressFormField } from "@/components/molecules/RepresentativeEmailAddressFormField";
import { SiteUrlFormField } from "@/components/molecules/SiteUrlFormField";
import { QualifiedInvoiceIssuerRegistrationNumberFormField } from "@/components/molecules/QualifiedInvoiceIssuerRegistrationNumberFormField";
import { InsuranceAgencyNameFormField } from "../molecules/InsuranceAgencyNameFormField";
import { COMPANY_OPTIONS_PREFECTURES } from "@/libs/constants";
import config from "@/libs/config";
import Link from "next/link";
import { linkStyle } from "@/styles/sharedStyle";
import { MainUserNameFormField } from "../molecules/MainUserNameFormField";
import { SelectAddressModal } from "@/components/organisms/SelectAddressModal";
import { BusinessTypeFormField } from "@/components/molecules/BusinessTypeFormField";
import { RepresentativeBirthDateFormField } from "@/components/molecules/RepresentativeBirthDateFormField";

export type RegisterCompanyInfoFormValues = {
  companyBusinessType: BusinessType;
  companyNameStoreName: string;
  companyNameStoreNameKana: string;
  corporateNumber: string;
  qualifiedInvoiceIssuerRegistrationNumber: string;
  qualifiedInvoiceIssuerRegistrationNumberCheck: boolean;
  industriesLarge: string;
  industriesMiddle: string;
  businessContent: string;
  foundationDate: string;
  capital: number;
  siteUrl: string;
  staffCount: string;
  settlementMonth: string;
  branchOfficeId: number;
  phoneNumber: string;
  postalCodeForward: string;
  postalCodeEnd: string;
  prefectures: string;
  municipality: string;
  blockBuildingName: string;
  representativeEmailAddress: string;
  representativeName: string;
  representativeBirthDate: string;
  mainUserName: string;
  mainUserPhoneNumber: string;
  insuranceAgencyCode: string;
  insuranceAgencyName: string;
};

type Props = {
  onSubmit: (formValues: RegisterCompanyInfoFormValues) => void;
  bankBranchOffices: BankBranchOffice[];
  isEdit: boolean;
  isContractAdmin: boolean;
};

export const RegisterCompanyInfoForm = ({
  onSubmit,
  bankBranchOffices,
  isEdit,
  isContractAdmin,
}: Props) => {
  const isSpMode = useIsSpMode();
  const { handleSubmit, formState, setValue } = useFormContext();
  const [searchPostalCode, setSearchPostalCode] = useState<string>(null);

  const onError = (errors: FieldErrors) => {
    // react-hook-form does not know how to handle multiple refs with the same name, which is how
    // BusinessTypeFormField is implemented.
    //
    // This means that when there is an error in BusinessTypeFormField, react-hook-form does not
    // know how to scroll to BusinessTypeFormField.
    //
    // This might be better solved by an implementation of FormRadioButton that looks like:
    //
    // <FormRadioButtonGroup name={name} ref={ref}>
    //   <FormRadioButton value={BusinessType.Corporate} label="法人" />
    //   <FormRadioButton value={BusinessType.Individual} label="個人事業主" />
    // </FormRadioButtonGroup>
    //
    // but I ran into some problems when doing this, and I wasn't sure if the issue was with
    // my implementation, or with Storybook and how we use FormDecorator in Storybook.
    //
    // Further, when researching this issue, it seems that for radio buttons, the current
    // implementation is the correct approach.
    //
    // So, we manually handle scrolling to companyBusinessType on error here.

    // for other errors, rely on react-hook-form's default behavior
    if (!errors.companyBusinessType) return;

    // find all companyBusinessType radio buttons
    const elements = document.getElementsByName(
      (errors.companyBusinessType.ref as any)?.name
    );
    // extra safety check
    if (elements.length === 0) return;

    // scroll and focus on the first radio button
    const elem = elements[0];
    elem.scrollIntoView({ behavior: "smooth" });
    elem.focus();
  };

  return (
    <>
      <div css={container}>
        <div css={!isSpMode ? box : spModeBox}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            css={form}
            autoComplete="off"
          >
            <div css={!isSpMode ? inputList : spModeInputList}>
              <div css={sectionContainer}>
                <div css={sectionTitle}>
                  <Font variant="mb">基本情報</Font>
                </div>
                <div css={listItem}>
                  <BusinessTypeFormField name="companyBusinessType" />
                </div>
                <div css={listItem}>
                  <CompanyNameFormField
                    name="companyNameStoreName"
                    labelRequired={true}
                  />
                </div>
                <div css={listItem}>
                  <KanaCompanyNameFormField
                    name="companyNameStoreNameKana"
                    labelRequired={false}
                  />
                </div>
                <div css={listItem}>
                  <CorporateNumberFormField
                    name="corporateNumber"
                    labelRequired={false}
                  />
                </div>
                <div css={listItem}>
                  <QualifiedInvoiceIssuerRegistrationNumberFormField
                    name="qualifiedInvoiceIssuerRegistrationNumber"
                    labelRequired={false}
                  />
                </div>
                <div css={listItem}>
                  <IndustriesFormField
                    nameLarge="industriesLarge"
                    nameMiddle="industriesMiddle"
                    labelRequired={true}
                  />
                </div>
                <div css={listItem}>
                  <BusinessContentFormField
                    name="businessContent"
                    labelRequired={false}
                  />
                </div>
                <div css={listItem}>
                  <FoundationDateFormField
                    name="foundationDate"
                    labelRequired={false}
                  />
                </div>
                <div css={listItem}>
                  <CapitalFormField name="capital" labelRequired={false} />
                </div>
                <div css={listItem}>
                  <StaffCountFormField
                    name="staffCount"
                    labelRequired={false}
                  />
                </div>
                <div css={listItem}>
                  <SettlementMonthFormField
                    name="settlementMonth"
                    labelRequired={true}
                  />
                </div>
                {isEdit && (
                  <div css={listItem}>
                    <SiteUrlFormField name="siteUrl" labelRequired={false} />
                  </div>
                )}
                {config.IS_INDUSTRY_TYPE_BANK && (
                  <div css={listItem}>
                    <BranchOfficeFormField
                      name="branchOfficeId"
                      labelRequired={true}
                      branchOffices={bankBranchOffices}
                      isDisabled={isEdit}
                    />
                  </div>
                )}
              </div>

              <div css={sectionContainer}>
                <div css={sectionTitle}>
                  <Font variant="mb">住所</Font>
                </div>
                <div css={listItem}>
                  <PostalCodeFormField
                    nameForward="postalCodeForward"
                    nameEnd="postalCodeEnd"
                    labelRequired={true}
                    onClickSearchAddress={(postalCode) => {
                      setSearchPostalCode(postalCode);
                    }}
                  />
                </div>
                <div css={listItem}>
                  <PrefecturesFormField
                    name="prefectures"
                    labelRequired={true}
                  />
                </div>
                <div css={listItem}>
                  <MunicipalityFormField
                    name="municipality"
                    labelRequired={true}
                  />
                </div>
                <div css={listItem}>
                  <BlockBuildingNameFormField
                    name="blockBuildingName"
                    labelRequired={true}
                  />
                </div>
              </div>

              <div css={sectionContainer}>
                <div css={sectionTitle}>
                  <Font variant="mb">代表者の情報</Font>
                </div>
                <div css={listItem}>
                  <RepresentativeNameFormField
                    name="representativeName"
                    labelRequired={true}
                  />
                </div>
                {isEdit && (
                  <div css={listItem}>
                    <RepresentativeBirthDateFormField
                      name="representativeBirthDate"
                      labelRequired={false}
                    />
                  </div>
                )}
                <div css={listItem}>
                  <PhoneNumberFormField
                    name="phoneNumber"
                    labelRequired={true}
                    label="代表電話番号"
                  />
                </div>
                {isEdit && (
                  <div css={listItem}>
                    <RepresentativeEmailAddressFormField
                      name="representativeEmailAddress"
                      labelRequired={false}
                    />
                  </div>
                )}
              </div>

              <div css={listItem}>
                <div css={sectionTitle}>
                  <Font variant="mb">Mikatano シリーズの主な利用者</Font>
                </div>
                <div css={sectionDescription}>
                  {/* prettier-ignore */}
                  <span css={sectionDescriptionText}>Mikatano シリーズを主に利用する方の氏名と電話番号を教えてください。</span>
                  <br />
                  <span css={sectionDescriptionText}>
                    活用サポートのために、ご連絡させていただくことがあります。
                  </span>
                </div>
                <div css={listItem}>
                  <MainUserNameFormField
                    name="mainUserName"
                    labelRequired={true}
                  />
                </div>
                <div css={listItem}>
                  <PhoneNumberFormField
                    name="mainUserPhoneNumber"
                    labelRequired={true}
                    annotationMessage="利用者の方に繋がりやすい電話番号を入力してください"
                  />
                </div>
              </div>

              {config.IS_INDUSTRY_TYPE_INSURANCE && (
                <>
                  <div css={listItem}>
                    <div css={sectionTitle}>
                      <Font variant="mb">その他</Font>
                    </div>
                    <div css={sectionDescription}>
                      {/* prettier-ignore */}
                      <span css={sectionDescriptionText}>
                      Mikatano シリーズを紹介された店舗から指定された、代理店名と代理店コードを入力してください。
                    </span>
                    </div>
                    <div css={listItem}>
                      <InsuranceAgencyCodeFormField
                        name="insuranceAgencyCode"
                        labelRequired={!isEdit}
                        disabled={isEdit}
                      />
                    </div>
                    <div css={listItem}>
                      <InsuranceAgencyNameFormField
                        name="insuranceAgencyName"
                        labelRequired={!isEdit}
                        disabled={isEdit}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div css={buttonWrapper}>
              {isEdit && <CancelButton />}
              <Button
                type="submit"
                variant="primary"
                css={buttonMargin}
                disabled={formState.isSubmitting}
              >
                {isEdit ? "保存" : "登録"}
              </Button>
            </div>
          </form>
        </div>
        {isEdit && isContractAdmin && (
          <div css={!isSpMode ? linkWrapper : spModeLinkWrapper}>
            <Link href="/accept-company-withdrawal">
              <a css={linkStyle}>Mikatano シリーズから退会する</a>
            </Link>
          </div>
        )}
      </div>
      {/* Modal */}
      {searchPostalCode !== null && (
        <SelectAddressModal
          postalCode={searchPostalCode}
          onClose={() => setSearchPostalCode(null)}
          onConfirm={(address) => {
            const prefecture = COMPANY_OPTIONS_PREFECTURES.find(
              (ele) => ele.label === address.prefectures
            ).value;
            setValue("prefectures", prefecture);
            setValue("municipality", address.municipality);
            setValue("blockBuildingName", address.blockBuildingName);
          }}
        />
      )}
    </>
  );
};

const container = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const box = css({
  width: "640px",
  minHeight: "160px",
  padding: "32px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const spModeBox = css({
  width: "100%",
  minHeight: "160px",
  padding: "16px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const form = css({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const inputList = css({
  flex: 1,
});

const spModeInputList = css({
  flex: 1,
});

const listItem = css({
  marginBottom: "16px",
});

const buttonWrapper = css({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "8px",
});
const buttonMargin = css({
  marginLeft: "16px",
});

const sectionContainer = css({
  marginBottom: "16px",
});

const sectionTitle = css({
  padding: "16px 0px",
});

const sectionDescription = css({
  marginBottom: "16px",
});

const sectionDescriptionText = css({
  fontSize: "14px",
  fontWeight: "300",
  lineHeight: "150%",
});

const linkWrapper = css({
  display: "flex",
  justifyContent: "flex-start",
  width: "640px",
  marginTop: "40px",
});

const spModeLinkWrapper = css({
  display: "flex",
  justifyContent: "flex-start",
  width: "100%",
  marginTop: "30px",
});
